import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CustomButton, icon, ConsumptionTrackingSidePanel } from "../../../../../../../../common/components";
import styles from "./DSIQuotasBlock.module.css";
import { BackOfficeService } from "../../../../../../../../api";
import { useApi } from "../../../../../../../../common/hooks";
import { isNonEmptyObject, roundTo } from "../../../../../../../../common/utils";
import { QuotaAllocationDialog } from "./components";
import { translate } from "../../../../../../../../common/providers";

const allowedIntervals = ["DAILY", "WEEKLY"];
const DSIQuotasBlock = ({ selectedCompany }) => {
	const { call: getAllTokenQuotas } = useApi(BackOfficeService.getAllTokenQuotas);
	const { call: upsertTokenQuotas } = useApi(BackOfficeService.upsertTokenQuotas);
	const { call: getAllTokenConsumptions } = useApi(BackOfficeService.getAllTokenConsumptions);
	const { call: addQuotaExtention } = useApi(BackOfficeService.addQuotaExtention);
	const [openQuotaAllocationDialog, setOpenQuotaAllocationDialog] = useState(false);
	const [openConsumptionTrackingSP, setOpenConsumptionTrackingSP] = useState(false);
	const [quota, setQuota] = useState(null);
	const [consumption, setConsumption] = useState(null);
	const { dailyLimit, weeklyLimit } = useMemo(
		() => ({
			dailyLimit: quota?.find((q) => q.type === "DAILY")?.token || 0,
			weeklyLimit: quota?.find((q) => q.type === "WEEKLY")?.token || 0,
		}),
		[quota]
	);
	const handleGetTokenConsumptions = useCallback(() => {
		getAllTokenConsumptions({ companyId: selectedCompany.id })
			.then((data) => {
				setConsumption(data);
			})
			.catch(console.error);
	}, [getAllTokenConsumptions, selectedCompany]);
	const handleGetQuotas = useCallback(() => {
		getAllTokenQuotas({ companyId: selectedCompany.id })
			.then((data) => {
				setQuota(data);
			})
			.catch(console.error);
	}, [getAllTokenQuotas, selectedCompany]);
	useEffect(() => {
		if (!selectedCompany) {
			return;
		}
		handleGetQuotas();
		handleGetTokenConsumptions();
	}, [selectedCompany, handleGetQuotas, handleGetTokenConsumptions]);
	const handleOpenQuotaAllocationDialog = () => {
		setOpenQuotaAllocationDialog(true);
	};
	const handleCloseQuotaAllocationDialog = () => {
		setOpenQuotaAllocationDialog(false);
	};
	const handleOpenConsumptionTrackingSP = () => {
		setOpenConsumptionTrackingSP(true);
	};
	const handleCloseConsumptionTrackingSP = () => {
		setOpenConsumptionTrackingSP(false);
	};
	const handleChangeAllocatedTokens = (newQuota) => {
		const payload = newQuota.map((q) => ({ ...q, token: q.token * 1000000 }));
		upsertTokenQuotas({
			companyId: selectedCompany.id,
			payload,
		})
			.then(() => {
				setQuota(payload);
				handleGetTokenConsumptions();
			})
			.catch(console.error);
	};
	const handleExtendQuota = (quotaId, key, token, validUntil) => {
		addQuotaExtention({ quotaId }, { token, validUntil })
			.then(() => {
				setConsumption((prev) =>
					prev.map((p) => {
						if (p.type === key) {
							return {
								...p,
								totalLimit: quota.find((q) => q.type === key).token + token,
								extension: {
									tokenExtensions: token,
									validUntil,
								},
							};
						}
						return p;
					})
				);
			})
			.catch(console.error);
	};
	return (
		<>
			<div className={styles.container}>
				<div className={styles.title}>{translate("dsi.dsi-company.quotas.management.title")}</div>
				<div className={styles.infoContainer}>
					<div className={styles.limitDescription}>
						{translate("dsi.dsi-company.quotas.management.subtitle")}
					</div>
					<div className={styles.limitText}>
						{roundTo((dailyLimit || 0) / 1000000, 1)}M/
						{roundTo((weeklyLimit || 0) / 1000000, 1)}M
					</div>
				</div>
				<div>
					<CustomButton
						className={styles.btn}
						startIcon={icon.faHashtag}
						variant="contained"
						onClick={handleOpenQuotaAllocationDialog}
					>
						{translate("dsi.dsi-company.quotas.management.btn.update")}
					</CustomButton>
					<CustomButton
						className={styles.btn}
						disabled={!isNonEmptyObject(quota)}
						variant="outlined"
						onClick={handleOpenConsumptionTrackingSP}
					>
						{translate("dsi.dsi-company.quotas.management.btn.follow-up")}
					</CustomButton>
				</div>
			</div>
			<QuotaAllocationDialog
				allowedIntervals={allowedIntervals}
				companyName={selectedCompany.companyName}
				open={openQuotaAllocationDialog}
				quota={quota}
				onClose={handleCloseQuotaAllocationDialog}
				onSubmit={handleChangeAllocatedTokens}
			/>
			<ConsumptionTrackingSidePanel
				allowedIntervals={allowedIntervals}
				companyName={selectedCompany.companyName}
				consumption={consumption}
				open={openConsumptionTrackingSP}
				onClose={handleCloseConsumptionTrackingSP}
				onExtendQuota={handleExtendQuota}
			/>
		</>
	);
};

export default DSIQuotasBlock;
